import { ArClientType, ArEmailAgingBucket } from '@repo/types';

export interface AgingBucketOption {
  value: ArEmailAgingBucket;
  label: string;
}

export interface AgingBucketBounds {
  daysPastDue?: number;
  daysPastDueCap?: number;
}

/**
 * Map an aging-bucket enum to days-past-due bounds.
 *
 * Mirrors `apps/api/src/modules/ar-collections/services/utils/agingBucketRange.ts`
 * but translates the API's `gte`/`lte` (which are bounds on days-past-due) into
 * the FE's scalar `{ daysPastDue, daysPastDueCap }` shape:
 *  - API `range.gte` (DPD >= N) => FE `daysPastDue = N`
 *  - API `range.lte` (DPD <= M) => FE `daysPastDueCap = M`
 */
export function getAgingBucketBounds(bucket: ArEmailAgingBucket): AgingBucketBounds {
  switch (bucket) {
    case ArEmailAgingBucket.ALL:
      return { daysPastDue: 1 };
    case ArEmailAgingBucket.CURRENT:
      return { daysPastDueCap: 0 };
    case ArEmailAgingBucket.DAYS_31_40:
      return { daysPastDue: 31, daysPastDueCap: 40 };
    case ArEmailAgingBucket.DAYS_41_50:
      return { daysPastDue: 41, daysPastDueCap: 50 };
    case ArEmailAgingBucket.DAYS_51_60:
      return { daysPastDue: 51, daysPastDueCap: 60 };
    case ArEmailAgingBucket.DAYS_61_PLUS:
      return { daysPastDue: 61 };
    case ArEmailAgingBucket.DAYS_45_54:
      return { daysPastDue: 45, daysPastDueCap: 54 };
    case ArEmailAgingBucket.DAYS_55_64:
      return { daysPastDue: 55, daysPastDueCap: 64 };
    case ArEmailAgingBucket.DAYS_65_74:
      return { daysPastDue: 65, daysPastDueCap: 74 };
    case ArEmailAgingBucket.DAYS_75_PLUS:
      return { daysPastDue: 75 };
    default:
      return {};
  }
}

export const agingBucketsByClientType: Record<ArClientType, AgingBucketOption[]> = {
  [ArClientType.PRIVATE]: [
    { value: ArEmailAgingBucket.ALL, label: 'All' },
    { value: ArEmailAgingBucket.CURRENT, label: 'Current' },
    { value: ArEmailAgingBucket.DAYS_31_40, label: '31-40 days' },
    { value: ArEmailAgingBucket.DAYS_41_50, label: '41-50 days' },
    { value: ArEmailAgingBucket.DAYS_51_60, label: '51-60 days' },
    { value: ArEmailAgingBucket.DAYS_61_PLUS, label: '61+ days' },
  ],
  [ArClientType.INSURANCE]: [
    { value: ArEmailAgingBucket.ALL, label: 'All' },
    { value: ArEmailAgingBucket.CURRENT, label: 'Current' },
    { value: ArEmailAgingBucket.DAYS_45_54, label: '45-54 days' },
    { value: ArEmailAgingBucket.DAYS_55_64, label: '55-64 days' },
    { value: ArEmailAgingBucket.DAYS_65_74, label: '65-74 days' },
    { value: ArEmailAgingBucket.DAYS_75_PLUS, label: '75+ days' },
  ],
};

export const defaultAgingBucketByClientType: Record<ArClientType, ArEmailAgingBucket> = {
  [ArClientType.PRIVATE]: ArEmailAgingBucket.DAYS_31_40,
  [ArClientType.INSURANCE]: ArEmailAgingBucket.DAYS_45_54,
};
